import { type FC, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PdpRespMarketplaceLoadingCarousel } from '../PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel';

import { type PdpRespMarketplacePersonalizedCarouselLazy_viewer$key } from './__generated__/PdpRespMarketplacePersonalizedCarouselLazy_viewer.graphql';
import { type PdpRespMarketplacePersonalizedCarouselLazy_user$key } from './__generated__/PdpRespMarketplacePersonalizedCarouselLazy_user.graphql';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';

const PdpSharedPersonalizationCarousel = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedPersonalizationCarousel" */ '../../shared/PdpSharedPersonalizationCarousel/PdpSharedPersonalizationCarousel'
        )
);

type Props = {
    viewer: PdpRespMarketplacePersonalizedCarouselLazy_viewer$key;
    user: PdpRespMarketplacePersonalizedCarouselLazy_user$key | null;
    currency: string;
    afterDisplay?: boolean;
};

const viewerFragment = graphql`
    fragment PdpRespMarketplacePersonalizedCarouselLazy_viewer on Viewer {
        ...PdpSharedPersonalizationCarousel_viewer @include(if: $afterDisplay)
    }
`;

const userFragment = graphql`
    fragment PdpRespMarketplacePersonalizedCarouselLazy_user on User {
        ...PdpSharedPersonalizationCarousel_user @include(if: $afterDisplay)
    }
`;

export const PdpRespMarketplacePersonalizedCarouselLazy: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    currency,
    afterDisplay,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const { isBot } = useServerVarsContext();

    if (isBot) {
        return null;
    }

    if (!afterDisplay) {
        return <PdpRespMarketplaceLoadingCarousel />;
    }

    return (
        <Suspense fallback={<PdpRespMarketplaceLoadingCarousel />}>
            <PdpSharedPersonalizationCarousel viewer={viewer} user={user} currency={currency} />
        </Suspense>
    );
};
